import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import {
  X, Calendar, User, FileText, Building, Save, Info, AlertCircle
} from "lucide-react";
import { submitCapacityActivity } from "../../api/capacityActivityApi";

export default function CapacityActivityModal({ isOpen, onClose, onSuccess, initialData }) {
  const { register, handleSubmit, setValue, control, formState: { errors }, reset } = useForm();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const modalRef = useRef(null);

  const departments = [
    { value: "Human Resources", label: "Human Resources" },
    { value: "Programs", label: "Programs" },
    { value: "Operations", label: "Operations" },
    { value: "Admin", label: "Admin" },
    { value: "Finance", label: "Finance" },
  ];

  useEffect(() => {
    if (isOpen && initialData) {
      setValue("title", initialData.title || "");
      setValue("description", initialData.description || "");
      setValue("date", initialData.date?.slice(0, 10) || "");
      setValue("trainer", initialData.trainer || "");

      const department = departments.find(dept => dept.value === initialData.department);
      setSelectedDepartment(department || null);
      setValue("department", initialData.department || "");
    } else if (isOpen) {
      reset();
      setSelectedDepartment(null);
    }
  }, [isOpen, initialData, setValue, reset]);

  useEffect(() => {
    if (selectedDepartment) {
      setValue("department", selectedDepartment.value);
    }
  }, [selectedDepartment, setValue]);

  const handleDepartmentChange = (selected) => {
    setSelectedDepartment(selected);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      await submitCapacityActivity({ ...data, _id: initialData?._id }, !!initialData);
      setSuccessMessage("Capacity Activity saved successfully!");
      setError("");
      if (onSuccess) onSuccess();
      setTimeout(() => {
        setSuccessMessage("");
        onClose();
      }, 1000);
    } catch (err) {
      console.error(err);
      setError("Failed to save the Capacity Activity.");
    } finally {
      setLoading(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore clicks inside modal OR inside React Select dropdowns
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        !e.target.closest(".react-select__menu") // <-- this line added
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);


  // Enhanced keyboard navigation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Enhanced Header with Orange Background */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <Calendar className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">
                  {initialData ? "Edit" : "Add"} Capacity Building Activity
                </h2>
                <p className="text-lg text-black-100 mt-1">
                  Training Management
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Enhanced Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Error</p>
                  <p className="mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5">✓</div>
                <div className="text-sm text-green-700">
                  <p className="font-medium">Success</p>
                  <p className="mt-1">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} id="capacity-form" className="space-y-6">
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                <span>Activity Title</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Required
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register("title", {
                    required: "Activity title is required",
                    minLength: { value: 3, message: "Title must be at least 3 characters" }
                  })}
                  type="text"
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  placeholder="Enter activity title"
                />
              </div>
              {errors.title && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.title.message}</span>
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                <span>Description</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Required
                </span>
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register("description", {
                    required: "Description is required",
                    minLength: { value: 10, message: "Description must be at least 10 characters" }
                  })}
                  rows={4}
                  className="w-full pl-10 pr-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none"
                  placeholder="Describe this activity..."
                />
              </div>
              {errors.description && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.description.message}</span>
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Date</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register("date", {
                      required: "Date is required"
                    })}
                    type="date"
                    className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.date ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  />
                </div>
                {errors.date && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.date.message}</span>
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                  <span>Department</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Required
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-gray-400" />
                  </div>
                  <Select
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    options={departments}
                    className="w-full rounded-xl"
                    placeholder="Select Department"
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.75rem',
                        minHeight: '44px',
                        paddingLeft: '42px',
                        borderColor: state.isFocused || errors.department ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.75rem',
                        border: '1px solid #E5E7EB'
                      })
                    }}
                  />
                </div>
                <input type="hidden" {...register("department", { required: "Department is required" })} value={selectedDepartment?.value || ""} />
                {errors.department && (
                  <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.department.message}</span>
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                <span>Trainer</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Required
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register("trainer", {
                    required: "Trainer name is required",
                    minLength: { value: 2, message: "Trainer name must be at least 2 characters" }
                  })}
                  type="text"
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-xl border ${errors.trainer ? 'border-red-300 bg-red-50' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  placeholder="Enter trainer name"
                />
              </div>
              {errors.trainer && (
                <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.trainer.message}</span>
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Enhanced Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Info className="w-4 h-4 text-orange-500" />
                <span>All fields marked as required must be completed</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                type="submit"
                form="capacity-form"
                disabled={loading}
                className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${loading
                  ? 'bg-orange-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                  }`}
              >
                {loading ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Save className="w-5 h-5" />
                    <span>Save</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}